/**
 * The complexity error tally.
 *
 * Complexity grading keeps its own tally, deliberately separate from
 * `EventStream`'s accuracy scoring. `EventStream.serErrorScore` weights a
 * semi-error at 0.7 for accuracy purposes; the complexity model's thresholds
 * (`greenMax = 1.0`, `yellowMax = 3.0`) were calibrated in the oracle against
 * red = 1.0 / yellow = 0.5, so consuming `ErrorScore` directly would silently
 * shift every verdict boundary.
 *
 * We do NOT re-derive severity from MIDI — we read the onset/offset states
 * `EventStream` has already computed, applying the same "full error is
 * terminal" rule it uses when painting (`makeNoteColor`). This keeps the two in
 * agreement: a note the user sees as red always tallies as red.
 *
 * Evaluating once at phrase end (rather than incrementally as notes are
 * painted) also removes any risk of double-counting a note that gets repainted.
 */

import { MidiEventState } from 'Types';
import { isErrorState, isSemiErrorState } from 'Utils';

/** Complexity weight of a single note. */
export enum NoteSeverity {
  Correct = 'correct',
  /** Timing error — painted yellow. */
  Semi = 'semi',
  /** Wrong or missed note — painted red. Terminal. */
  Error = 'error',
}

/** Complexity error weights. Red 1.0, yellow 0.5 — see module comment. */
export const SEVERITY_WEIGHT: Readonly<Record<NoteSeverity, number>> = Object.freeze({
  [NoteSeverity.Correct]: 0,
  [NoteSeverity.Semi]: 0.5,
  [NoteSeverity.Error]: 1.0,
});

/** The minimum a graded note must expose. Satisfied by `NoteEvent`. */
export interface GradedNote {
  OnsetState: MidiEventState;
  OffsetState: MidiEventState;
}

/**
 * Severity of one note.
 *
 * Mirrors `EventStream.makeNoteColor`: a full error on either onset or offset
 * is terminal and outranks any semi-error, exactly as `lockedFullError` does
 * when painting.
 */
export function severityOf(note: GradedNote): NoteSeverity {
  if (isErrorState(note.OnsetState) || isErrorState(note.OffsetState)) {
    return NoteSeverity.Error;
  }
  if (isSemiErrorState(note.OnsetState) || isSemiErrorState(note.OffsetState)) {
    return NoteSeverity.Semi;
  }
  return NoteSeverity.Correct;
}

/**
 * Total complexity error for a phrase: `red * 1.0 + yellow * 0.5`.
 *
 * Feed the result to `classify()` to get the phrase's verdict.
 */
export function tallyPhraseErrors(notes: readonly GradedNote[]): number {
  return notes.reduce((total, note) => total + SEVERITY_WEIGHT[severityOf(note)], 0);
}
